import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  DataGridPremiumProps,
  getGridStringOperators,
  GridCellParams,
  GridColumnGroupingModel,
  GridEventListener,
  GridFilterModel,
  GridPinnedColumnFields,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
  Toolbar,
} from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { AcceptedData } from 'export-to-csv/output/lib/types'
import { MUITableToolBarComponent } from './MUITableToolbar'
import { usePermissions } from 'Features/Scheduler/Scheduler.data'
import { BulkUpdateModal, ExtendedGridColDef } from './BulkUpdateModal'
import {
  discardChanges,
  saveChangesToServer,
} from 'Common/Components/MUITable.methods'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { searchTextOperator } from 'Common/MUITableV2/SearchText.operator'
import { useBlocker } from 'react-router-dom'
import { NavigationModal } from './NavigationModal'
import {
  autoHeight,
  setTableContainerHeight,
  toSentenceCase,
} from 'Common/utils/utils'
import { DropZone } from 'Features/SystemOfRecords/Tabs/DropZone'

export type BaseTableProps<T> = {
  data: T[]
  isLoading: boolean
  updateTableData: (changedRows: Record<number, T>) => void
  processRow?: (row: T, oldRow: T) => T
  updateUnsavedRows?: (
    unsavedChangesRef: RefObject<{
      unsavedRows: Record<GridRowId, T>
      rowsBeforeChange: Record<GridRowId, T>
    }>
  ) => void
  uploadData?: (file: File) => void
  isUploading?: boolean
  isSaving: boolean
  checkBoxSelection?: boolean
  showTopToolBar?: boolean
  columns?: ExtendedGridColDef[]
  showTableToolBar?: boolean
  handleBulkUpdateButtonClick?: () => void
  downloadFileName?: string
  handleRowClick?: GridEventListener<'rowClick'>
  pinnedColumns?: string[]
  toolBarChildren?: ReactNode
  isCellEditable?: (params: GridCellParams) => boolean
  columnGroupingModel?: GridColumnGroupingModel
  rowGroupingModel?: GridRowGroupingModel
  groupingColDef?: Record<string, unknown>
  sx?: SxProps<Theme>
  customToolbar?: (props: {
    selectedRows: GridRowSelectionModel
    hasUnsavedRows: boolean
    isSaving: boolean
    data: T[]
    fileName: string
  }) => () => ReactNode
  // New props for CombinedView support
  onCellClick?: GridEventListener<'cellClick'>
  hideFooter?: boolean
  rowHeight?: number
  initialState?: DataGridPremiumProps['initialState']
  onMouseMove?: (event: React.MouseEvent<HTMLDivElement>) => void
  apiRefCallback?: (apiRef: RefObject<GridApiPremium>) => void
  getRowClassName?: DataGridPremiumProps['getRowClassName']
  // Master-Detail Panel support
  getDetailPanelContent?: (
    params: GridRowParams<GridValidRowModel>
  ) => ReactNode
  getDetailPanelHeight?: DataGridPremiumProps['getDetailPanelHeight']
  detailPanelExpandedRowIds?: GridRowId[]
  onDetailPanelExpandedRowIdsChange?: (newIds: GridRowId[]) => void
  // Aggregation support
  aggregationFunctions?: DataGridPremiumProps['aggregationFunctions']
  // getRowId support
  getRowId?: DataGridPremiumProps['getRowId']
  density?: 'compact' | 'standard' | 'comfortable'
  // New props for DropZone integration
  multiStageUpload?: {
    uploadFile: (file: File) => void
    isUploading: boolean
    columns: ExtendedGridColDef[]
    updateRowsStatus: Record<string, unknown> | undefined
    originalData: T[]
    predicate: string[] | undefined
    dataConfig: { required: string[]; optional: string[] }
  }
  tableHeight?: number
  onFilterModelChange?: (model: GridFilterModel) => GridFilterModel
  userDefinedFilterModel?: GridFilterModel
  customDownloadDataMethod?: (tableData: T[]) => T[]
  /** Adds or rewrites columns of one downloaded row, after the visible
   *  columns have been read (so filtering, sorting and column choice still
   *  apply). For a value no grid column shows — Specific Time's windows. */
  expandDownloadRow?: (
    out: Record<string, AcceptedData>,
    row: Record<string, unknown>
  ) => Record<string, AcceptedData>
  enableBlocker?: boolean
  onCheckboxSelectionChange?: (selectionModel: GridRowSelectionModel) => void
  onDeleteRows?: (selectedRows: T[]) => Promise<void>
  isDeleting?: boolean
  defaultGroupingExpansionDepth?: number
}

export type UnsavedChangesContextType = {
  unsavedChangesRef: RefObject<{
    unsavedRows: Record<GridRowId, unknown>
    rowsBeforeChange: Record<GridRowId, unknown>
  }> | null
  hasUnsavedRows: boolean
  setHasUnsavedRows: (value: boolean) => void
  undoRedoPointer: RefObject<number>
  undoRedoStack: RefObject<GridRowId[]>
  apiRef: RefObject<GridApiPremium>
  selectedRows: GridRowSelectionModel
}

const checkBoxSelectionColumnName = '__check__'

export const BaseTable = <T extends GridValidRowModel>({
  data,
  isLoading,
  updateTableData,
  processRow,
  uploadData,
  isUploading,
  isSaving,
  checkBoxSelection = false,
  //showTopToolBar = true,
  columns,
  multiStageUpload,
  // New props for CombinedView support
  onCellClick,
  hideFooter = false,
  rowHeight,
  initialState,
  onMouseMove,
  apiRefCallback,
  getRowClassName: customGetRowClassName,
  // Master-Detail Panel props
  getDetailPanelContent,
  getDetailPanelHeight,
  detailPanelExpandedRowIds,
  onDetailPanelExpandedRowIdsChange,
  // Aggregation props
  aggregationFunctions,
  getRowId,
  showTableToolBar = true,
  handleBulkUpdateButtonClick = () => {
    console.log('Bulk update button clicked')
  },
  downloadFileName = 'fileDownload',
  handleRowClick = undefined,
  pinnedColumns = [],
  //toolBarChildren,
  isCellEditable,
  columnGroupingModel = [],
  rowGroupingModel = [],
  groupingColDef = {},
  sx,
  customToolbar,
  density = 'compact',
  tableHeight = undefined,
  onFilterModelChange = undefined,
  userDefinedFilterModel = undefined,
  customDownloadDataMethod = undefined,
  expandDownloadRow = undefined,
  enableBlocker = true,
  onCheckboxSelectionChange = undefined,
  onDeleteRows = undefined,
  isDeleting = false,
  ...props
}: BaseTableProps<T> & { sx?: SxProps<Theme> }) => {
  const apiRef = useGridApiRef() as RefObject<GridApiPremium>

  // Call apiRefCallback if provided to allow external access to apiRef
  useEffect(() => {
    if (apiRefCallback) {
      apiRefCallback(apiRef)
    }
  }, [apiRefCallback])

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  })

  const [pinnedColumnsToLeft, setPinnedColumnsToLeft] = useState<string[]>([
    checkBoxSelectionColumnName,
    ...pinnedColumns,
  ])

  // Convert hasUnsavedRows from ref to state
  const [hasUnsavedRows, setHasUnsavedRows] = useState<boolean>(false)
  const blocker = useBlocker(enableBlocker && hasUnsavedRows)

  const handleCancel = () => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  const handleProceed = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  // Add density state for better handling of large datasets
  const [systemDensity, setSystemDensity] = useState<
    'compact' | 'standard' | 'comfortable'
  >(density)

  const unsavedChangesRef = useRef<{
    unsavedRows: Record<GridRowId, T>
    rowsBeforeChange: Record<GridRowId, T>
  }>({
    unsavedRows: {},
    rowsBeforeChange: {},
  })

  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false)

  // Handle bulk update values directly in BaseTable to avoid re-renders
  const handleBulkApply = (values: Record<string, unknown>) => {
    if (!Object.keys(values).length) return

    const updatedRows: GridValidRowModel[] = []

    // Handle different selection types
    if (selectedRows.type === 'include') {
      // Individual selection: iterate through selected IDs
      const selectedRowIds = selectedRows.ids || new Set()
      for (const rowId of selectedRowIds) {
        const oldRow = apiRef.current.getRow(rowId)
        if (!oldRow) continue

        const newRow = { ...oldRow, ...values }
        updatedRows.push(processRowUpdate(newRow, oldRow, { rowId }))
      }
    } else if (selectedRows.type === 'exclude') {
      // Select All: iterate through all rows except excluded ones
      const excludedIds = selectedRows.ids || new Set()
      for (const row of data) {
        if (excludedIds.has(row.id)) continue // Skip excluded rows

        const oldRow = apiRef.current.getRow(row.id)
        if (!oldRow) continue

        const newRow = { ...oldRow, ...values }
        updatedRows.push(processRowUpdate(newRow, oldRow, { rowId: row.id }))
      }
    }

    apiRef.current.updateRows(updatedRows)
    setShowBulkUpdateModal(false)
  }

  const onBulkUpdateButtonClick = useCallback(() => {
    setShowBulkUpdateModal(true)
    handleBulkUpdateButtonClick()
  }, [handleBulkUpdateButtonClick])

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [pendingDeleteRows, setPendingDeleteRows] = useState<T[]>([])

  const handleDeleteRows = useCallback(async () => {
    if (!onDeleteRows) return
    const rows: T[] = []
    if (selectedRows.type === 'include') {
      for (const rowId of selectedRows.ids) {
        const row = apiRef.current.getRow(rowId)
        if (row) rows.push(row as T)
      }
    } else {
      const excludedIds = selectedRows.ids
      for (const row of data) {
        if (!excludedIds.has(row.id)) rows.push(row)
      }
    }
    if (rows.length === 0) return
    setPendingDeleteRows(rows)
    setDeleteConfirmOpen(true)
  }, [onDeleteRows, selectedRows, data, apiRef])

  const confirmDelete = useCallback(async () => {
    if (!onDeleteRows || pendingDeleteRows.length === 0) return
    setDeleteConfirmOpen(false)
    try {
      await onDeleteRows(pendingDeleteRows)
      setSelectedRows({ type: 'include', ids: new Set() })
    } catch {
      // Selection is preserved on failure so the user can retry
    }
    setPendingDeleteRows([])
  }, [onDeleteRows, pendingDeleteRows])

  const undoRedoPointer = useRef<number>(-1)
  // This is to keep track of the rows are changed in order.
  // We keep the id of the row, and that points to either unsavedRows or rowsBeforeChange
  const undoRedoStack = useRef<GridRowId[]>([])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'z') {
        if (undoRedoPointer.current === -1) {
          return
        }
        if (undoRedoPointer.current >= undoRedoStack.current.length) {
          undoRedoPointer.current = undoRedoStack.current.length - 1
        }

        const undoObj =
          unsavedChangesRef.current.rowsBeforeChange[
            undoRedoStack.current[undoRedoPointer.current]
          ]
        if (!undoObj) {
          return
        }
        apiRef.current?.updateRows([undoObj])
        undoRedoPointer.current--

        // Check if there are still unsaved rows after undo
        if (undoRedoPointer.current === -1) {
          setHasUnsavedRows(false)
        }
      } else if (event.ctrlKey && event.key === 'y') {
        // Redo operation
        if (undoRedoPointer.current === undoRedoStack.current.length) {
          return
        }
        if (undoRedoPointer.current === -1) {
          undoRedoPointer.current = 0
        }

        const redoObj =
          unsavedChangesRef.current.unsavedRows[
            undoRedoStack.current[undoRedoPointer.current]
          ]
        if (!redoObj) {
          return
        }
        apiRef.current?.updateRows([redoObj])
        undoRedoPointer.current++
        // If we redo any changes, set hasUnsavedRows to true
        setHasUnsavedRows(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [apiRef])

  const handleUndo = useCallback(() => {
    if (undoRedoPointer.current === -1) return
    if (undoRedoPointer.current >= undoRedoStack.current.length) {
      undoRedoPointer.current = undoRedoStack.current.length - 1
    }
    const undoObj =
      unsavedChangesRef.current.rowsBeforeChange[
        undoRedoStack.current[undoRedoPointer.current]
      ]
    if (!undoObj) return
    apiRef.current?.updateRows([undoObj])
    undoRedoPointer.current--
    if (undoRedoPointer.current === -1) setHasUnsavedRows(false)
  }, [apiRef])

  const handleRedo = useCallback(() => {
    if (undoRedoPointer.current >= undoRedoStack.current.length - 1) return
    if (undoRedoPointer.current === -1) undoRedoPointer.current = 0
    else undoRedoPointer.current++
    const redoObj =
      unsavedChangesRef.current.unsavedRows[
        undoRedoStack.current[undoRedoPointer.current]
      ]
    if (!redoObj) return
    apiRef.current?.updateRows([redoObj])
    setHasUnsavedRows(true)
  }, [apiRef])

  const processRowUpdate: NonNullable<
    DataGridPremiumProps['processRowUpdate']
  > = (newRow, oldRow) => {
    try {
      // Update hasUnsavedRows state instead of ref
      setHasUnsavedRows(true)
      const rowId = newRow.id
      // Implement later: add a method here update newRow, if the user wants to do specific changes.
      // newRow = updateRow(newRow)
      if (processRow) {
        // Can be used to apply any updates to other columns
        newRow = processRow(newRow, oldRow)
      }
      unsavedChangesRef.current.unsavedRows[rowId] = newRow
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow
      }
      undoRedoPointer.current = undoRedoPointer.current + 1
      undoRedoStack.current[undoRedoPointer.current] = rowId
      // If undoRedoPonter is not at the end of the stack, then remove all the elements after that.
      if (undoRedoPointer.current < undoRedoStack.current.length - 1) {
        undoRedoStack.current = undoRedoStack.current.slice(
          0,
          undoRedoPointer.current + 1
        )
      }

      return newRow
    } catch (error) {
      console.error('Error processing row update:', error)
      return oldRow
    }
  }

  const handlePinnedColumnChange = (pinnedColumns: GridPinnedColumnFields) => {
    if (!pinnedColumns.left) {
      return
    }
    const index = pinnedColumns.left.indexOf(checkBoxSelectionColumnName)
    if (index > -1) {
      pinnedColumns.left.splice(index, 1)
    }
    pinnedColumns.left.unshift(checkBoxSelectionColumnName)
    setPinnedColumnsToLeft(pinnedColumns.left)
  }

  // Create a stable hash of the data for memoization to avoid expensive recalculations
  // while still updating when data content actually changes
  const dataHash = useMemo(() => {
    if (!data?.length || !columns?.length) return ''

    // Only hash the fields that are actually used in string columns
    const stringColumnFields = columns
      .filter((column) => column?.type === 'string' && column?.field)
      .map((column) => column.field)

    if (!stringColumnFields.length) return ''

    // Create a minimal hash of only the relevant field values
    const relevantData = data
      .map((item) => stringColumnFields.map((field) => item[field]).join('|'))
      .join('||')

    // Use a simple hash to avoid JSON.stringify overhead for large datasets
    let hash = 0
    for (let i = 0; i < relevantData.length; i++) {
      const char = relevantData.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString()
  }, [data, columns])

  // Memoize unique values for each column separately to avoid recalculations
  const uniqueColumnValues = useMemo(() => {
    if (!columns || !data?.length) return {}

    // Create a map to store unique values for each string column
    const uniqueValues: Record<string, Set<unknown>> = {}

    // Find all string columns first
    const stringColumns = columns.filter((column) => column?.type === 'string')

    // Then compute unique values for each string column only once
    stringColumns.forEach((column) => {
      if (column?.field) {
        uniqueValues[column.field] = new Set(
          data
            .map((item) => item[column.field])
            .filter(
              (value) => value !== null && value !== undefined && value !== ''
            )
        )
      }
    })

    return uniqueValues
  }, [columns, dataHash]) // Now depends on actual data content via hash

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    userDefinedFilterModel ?? {
      items: [],
    }
  )

  // Then use the pre-computed unique values in compiledColumns
  const compiledColumns = useMemo(() => {
    if (!columns?.length) return []

    return columns.map((column) => {
      // Apply sentence case to headerName
      const sentenceCaseHeader = column.headerName
        ? toSentenceCase(column.headerName)
        : column.headerName

      if (column?.type === 'string' && column.field) {
        const uniqueSet = uniqueColumnValues[column.field]
        return {
          ...column,
          headerName: sentenceCaseHeader,
          filterOperators: [
            {
              ...searchTextOperator[0],
              InputComponentProps: {
                data: uniqueSet ? [...uniqueSet] : [],
                label: sentenceCaseHeader,
              },
            },
            ...getGridStringOperators(),
          ],
        }
      }
      return {
        ...column,
        headerName: sentenceCaseHeader,
      }
    })
  }, [columns, uniqueColumnValues, dataHash, filterModel])

  // ─── View-only gate ───────────────────────────────────────────────────────
  //
  // Every input table in the scheduler renders through this component, and
  // `MUITableToolbar` has no other consumer, so withdrawing the write controls
  // here covers all of them at once rather than per table.
  //
  // This decides what is OFFERED. What is ALLOWED is decided by
  // `security.auth.requires` on the service, which refuses the PUT/POST/DELETE
  // regardless of what the UI renders — so a stale or spoofed permission list
  // costs a confusing 403, never an unauthorised write.
  //
  // Download, quick-filter, undo/redo and column controls are deliberately
  // left alone: none of them reaches the server, and a view-only user still
  // needs to read, sort and export.
  const { canWrite } = usePermissions()

  // Suppressing the callback is what hides each control — the toolbar already
  // treats an absent handler as "do not render this button", so there is no
  // separate disabled state to add.
  const toolbarPropsRef = useRef<any>(null)
  toolbarPropsRef.current = {
    customToolbar,
    selectedRows,
    hasUnsavedRows,
    isSaving,
    data,
    downloadFileName,
    apiRef,
    unsavedChangesRef,
    updateTableData,
    undoRedoStack,
    undoRedoPointer,
    setHasUnsavedRows,
    setSelectedRows,
    onBulkUpdateButtonClick: canWrite ? onBulkUpdateButtonClick : undefined,
    onDeleteRows: canWrite ? onDeleteRows : undefined,
    handleDeleteRows,
    isDeleting,
    uploadData: canWrite ? uploadData : undefined,
    isUploading: isUploading || false,
    isLoading,
    customDownloadDataMethod,
    expandDownloadRow,
    handleUndo,
    handleRedo,
  }

  // Create a STABLE toolbar component — same function reference across all renders.
  // It reads changing values from the ref, so it always has fresh data without
  // changing its identity (which would cause DataGrid to remount the toolbar).
  const toolBarComponents = useMemo(() => {
    const StableToolbar = ({ children }: { children?: ReactNode }) => {
      const config = toolbarPropsRef.current

      if (config.customToolbar) {
        const CustomSlot = config.customToolbar({
          selectedRows: config.selectedRows,
          hasUnsavedRows: config.hasUnsavedRows,
          isSaving: config.isSaving,
          data: config.data,
          fileName: config.downloadFileName,
        })
        return <CustomSlot>{children}</CustomSlot>
      }

      if (config.isLoading) {
        return (
          <Toolbar>
            <CircularProgress size={18} />
          </Toolbar>
        )
      }

      return (
        <MUITableToolBarComponent
          hasUnsavedRows={config.hasUnsavedRows}
          // Read off the ref at render rather than mirrored into state: the
          // toolbar re-renders on every edit anyway (processRowUpdate sets
          // hasUnsavedRows), so this is current without adding a sixth place
          // that has to be kept in step with the unsaved-rows bookkeeping.
          unsavedCount={
            Object.keys(config.unsavedChangesRef.current?.unsavedRows ?? {})
              .length
          }
          isSaving={config.isSaving}
          saveChanges={() =>
            saveChangesToServer<T>(
              config.apiRef,
              config.unsavedChangesRef,
              config.updateTableData,
              config.undoRedoStack,
              config.undoRedoPointer,
              config.setHasUnsavedRows,
              config.setSelectedRows
            )
          }
          discardChanges={() =>
            discardChanges<T>(
              config.apiRef,
              config.unsavedChangesRef,
              config.undoRedoStack,
              config.undoRedoPointer,
              config.setHasUnsavedRows
            )
          }
          dataToDownload={config.data}
          fileName={config.downloadFileName}
          apiRef={config.apiRef}
          selectedRows={config.selectedRows}
          onBulkUpdateButtonClick={config.onBulkUpdateButtonClick}
          onDeleteRowsButtonClick={
            config.onDeleteRows ? config.handleDeleteRows : undefined
          }
          isDeleting={config.isDeleting}
          uploadData={config.uploadData}
          isUploading={config.isUploading}
          customDownloadDataMethod={config.customDownloadDataMethod}
          expandDownloadRow={config.expandDownloadRow}
          onUndo={config.handleUndo}
          onRedo={config.handleRedo}
          canUndo={true}
          canRedo={true}
        >
          {children}
        </MUITableToolBarComponent>
      )
    }
    return StableToolbar
  }, [])

  const [fileForUpload, setFileForUpload] = useState<File | null>(null)

  // Hidden file input for upload functionality - placed outside the DataGrid
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (files && files.length > 0 && uploadData) {
      //uploadData(files[0])
      setFileForUpload(files[0])
      // If we haven't defined multistage upload, Directly upload with uploadData
      if (!multiStageUpload) {
        uploadData(files[0])
      }
      event.target.value = ''
    }
  }

  return (
    <Box
      className="table-container-mui"
      display="flex"
      flexDirection="column"
      sx={{
        overflow: 'auto',
      }}
    >
      {/* Hidden file input for upload - outside DataGrid but accessible via permanent ID */}
      <input
        id="base-table-file-upload"
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {/* Render the BulkUpdateModal */}
      {enableBlocker && (
        <NavigationModal
          open={blocker.state === 'blocked'}
          handleCancel={handleCancel}
          handleProceed={handleProceed}
        />
      )}
      <BulkUpdateModal
        open={showBulkUpdateModal}
        onClose={() => setShowBulkUpdateModal(false)}
        selectedRows={selectedRows}
        columns={columns || []}
        onApply={handleBulkApply}
      />
      {multiStageUpload && (
        <DropZone
          uploadFile={multiStageUpload.uploadFile}
          isUploading={multiStageUpload.isUploading}
          columns={multiStageUpload.columns}
          updateRowsStatus={multiStageUpload.updateRowsStatus}
          originalData={multiStageUpload.originalData}
          predicate={multiStageUpload.predicate}
          dataConfig={multiStageUpload.dataConfig}
          file={fileForUpload}
        />
      )}
      <Box
        onMouseMove={onMouseMove}
        sx={{ position: 'relative', height: '100%' }}
      >
        <Table
          ref={
            !tableHeight
              ? autoHeight
              : (el) =>
                  setTableContainerHeight(
                    el as HTMLDivElement,
                    tableHeight as number
                  )
          }
          slots={{
            toolbar: toolBarComponents,
          }}
          showToolbar={showTableToolBar}
          checkboxSelection={checkBoxSelection}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectedRows(newRowSelectionModel)
            onCheckboxSelectionChange?.(newRowSelectionModel)
          }}
          pinnedColumns={{
            left: pinnedColumnsToLeft,
          }}
          onPinnedColumnsChange={handlePinnedColumnChange}
          apiRef={apiRef}
          disableRowSelectionOnClick
          loading={isLoading || isSaving}
          rows={data ?? []}
          columns={compiledColumns ?? []}
          getRowId={getRowId}
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination={!hideFooter}
          hideFooter={hideFooter}
          {...(rowHeight && { rowHeight })}
          {...(handleRowClick && { onRowClick: handleRowClick })}
          {...(onCellClick && { onCellClick })}
          initialState={
            initialState || {
              pagination: { paginationModel: { pageSize: 100 } },
            }
          }
          pageSizeOptions={[25, 50, 100]}
          // Disable virtualization to eliminate rendering glitches
          disableVirtualization={true}
          // Virtualization optimization - increase buffer to reduce glitches
          rowBufferPx={500}
          columnBufferPx={150}
          density={systemDensity}
          processRowUpdate={(
            newRow: GridValidRowModel,
            oldRow: GridValidRowModel
          ) => {
            return processRowUpdate(newRow, oldRow, { rowId: newRow.id })
          }}
          getRowClassName={(params) => {
            const classes = []

            // Add edited row class
            if (unsavedChangesRef.current.unsavedRows[params.id]) {
              classes.push('row--edited')
            }

            // Add alternating row class based on actual row index
            if (params.indexRelativeToCurrentPage !== undefined) {
              classes.push(
                params.indexRelativeToCurrentPage % 2 === 0
                  ? 'row--even'
                  : 'row--odd'
              )
            }

            // Add custom classes from prop if provided
            if (customGetRowClassName) {
              const customClass = customGetRowClassName(params)
              if (customClass) {
                classes.push(customClass)
              }
            }

            return classes.join(' ')
          }}
          {...((!canWrite || isCellEditable) && {
            // A withdrawn Save button is not enough on its own: without this
            // the grid still opens an editor on double-click, so a view-only
            // user could type into a cell and watch the edit evaporate.
            isCellEditable: canWrite ? isCellEditable : () => false,
          })}
          {...(columnGroupingModel &&
            columnGroupingModel.length > 0 && { columnGroupingModel })}
          {...(rowGroupingModel &&
            rowGroupingModel.length > 0 && { rowGroupingModel })}
          {...(Object.keys(groupingColDef || {}).length > 0 && {
            groupingColDef,
          })}
          defaultGroupingExpansionDepth={-1} // Can pass in if more specific cases
          // Master-Detail Panel props
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          detailPanelExpandedRowIds={
            detailPanelExpandedRowIds
              ? new Set(detailPanelExpandedRowIds)
              : undefined
          }
          onDetailPanelExpandedRowIdsChange={(newIds) => {
            if (onDetailPanelExpandedRowIdsChange) {
              onDetailPanelExpandedRowIdsChange(Array.from(newIds))
            }
          }}
          // Aggregation props - only pass if defined to avoid errors
          {...(aggregationFunctions && { aggregationFunctions })}
          sx={sx}
          editMode="cell"
          onDensityChange={(newDensity) => {
            setSystemDensity(newDensity)
          }}
          disablePivoting
          {...props}
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel) => {
            let filterModel = newFilterModel
            if (onFilterModelChange) {
              filterModel = onFilterModelChange(newFilterModel)
            }
            setFilterModel(filterModel)
          }}
        />
      </Box>
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setPendingDeleteRows([])
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 600, pb: 0.5 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            Delete {pendingDeleteRows.length} row
            {pendingDeleteRows.length !== 1 ? 's' : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false)
              setPendingDeleteRows([])
            }}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
